import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { DEFAULT_PRODUCT_PLAN_INPUT_MODEL } from "./constants/default-input-model";
import { excludeKeys, type ExcludeKeys, type ProductPlanFormModel } from "./type";
import { planInputZodSchema, type PlanInputZodSchemaType } from "./validation-schema";
import { useSendCustomMailSettingForCheckInBefore_00days } from "../../../_shared/model/repository/mutation/check-in-before-00-days";
import { useSendCustomMailSettingForCheckInBefore_01days } from "../../../_shared/model/repository/mutation/check-in-before-01-days";
import { useSendCustomMailSettingForCheckInBefore_07days } from "../../../_shared/model/repository/mutation/check-in-before-07-days";
import { useSendCustomMailSettingForCheckOutAfter_01days } from "../../../_shared/model/repository/mutation/check-out-after-01-days";
import { useSendCustomMailSettingForReservationCancel } from "../../../_shared/model/repository/mutation/reservation-cancel";
import { useSendCustomMailSettingForReservationCreate } from "../../../_shared/model/repository/mutation/reservation-create";
import { useSendCustomMailSettingForReservationUpdate } from "../../../_shared/model/repository/mutation/reservation-update";

import type { ProductPlanFormModelContextType } from "../ui/_shared/context/context";

type Props = {
	inputModel: ProductPlanFormModel["InputModel"];
	queryData: ProductPlanFormModel["QueryData"];
};

export const useInitProductPlanFormModelContext = ({
	inputModel: _inputModel,
	queryData,
}: Props): Omit<ProductPlanFormModelContextType, "queryData"> => {
	const hotelID = _inputModel.hotelID;
	const planID = _inputModel.planID;

	const defaultInputModel = DEFAULT_PRODUCT_PLAN_INPUT_MODEL({ planID, hotelID, queryData });
	const inputModel = _inputModel ?? defaultInputModel;

	const [viewModel, setViewModel] = useState(
		inputModelToViewModelConverter<
			ProductPlanFormModel["InputModel"],
			ExcludeKeys,
			ProductPlanFormModel["QueryData"],
			PlanInputZodSchemaType
		>({
			defaultInput: defaultInputModel,
			initInput: inputModel,
			currentInput: inputModel,
			excludeKeys: excludeKeys,
			zodSchema: planInputZodSchema,
		}),
	);

	const onSetViewModel: ProductPlanFormModelContextType["onSetViewModel"] = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce(
					(acc, cur) => {
						acc[cur.key] = cur.value;
						return acc;
					},
					{} as ProductPlanFormModel["ViewModel"],
				),
			} satisfies ProductPlanFormModel["ViewModel"];

			const currentInput = viewModelToInputModelConverter<
				ProductPlanFormModel["InputModel"],
				ExcludeKeys,
				ProductPlanFormModel["QueryData"]
			>(mergedViewModel);

			const newViewModel = inputModelToViewModelConverter<
				ProductPlanFormModel["InputModel"],
				ExcludeKeys,
				ProductPlanFormModel["QueryData"],
				PlanInputZodSchemaType
			>({
				defaultInput: defaultInputModel,
				initInput: inputModel,
				currentInput,
				excludeKeys: excludeKeys,
				zodSchema: planInputZodSchema,
			});

			setViewModel(newViewModel);
		},
		[defaultInputModel, inputModel, viewModel],
	);

	const resetViewModel = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	const { sendTestMailAtReservationCreate } = useSendCustomMailSettingForReservationCreate();
	const { sendTestMailAtReservationUpdate } = useSendCustomMailSettingForReservationUpdate();
	const { sendTestMailAtReservationCancel } = useSendCustomMailSettingForReservationCancel();
	const { sendTestMailAtCheckInBefore07Days } = useSendCustomMailSettingForCheckInBefore_07days();
	const { sendTestMailAtCheckInBefore01Days } = useSendCustomMailSettingForCheckInBefore_01days();
	const { sendTestMailAtCheckInBefore00Days } = useSendCustomMailSettingForCheckInBefore_00days();
	const { sendTestMailAtCheckOutAfter01Days } = useSendCustomMailSettingForCheckOutAfter_01days();

	return {
		viewModel,
		initInputModel: inputModel,
		onSetViewModel,
		resetViewModel,

		testEmailSender: {
			sendTestMailAtReservationCreate,
			sendTestMailAtReservationUpdate,
			sendTestMailAtReservationCancel,
			sendTestMailAtCheckInBefore07Days,
			sendTestMailAtCheckInBefore01Days,
			sendTestMailAtCheckInBefore00Days,
			sendTestMailAtCheckOutAfter01Days,
		},
	};
};
